import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { X, Trash2, Loader2, Clock, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { ConversationResult, convoToText } from "./ConversationResult";
import { PrintsThumbs } from "./PrintsPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VERDICT_STYLE = {
  MANDA: { bg: "#00FFA3", emoji: "🟢" },
  ESPERA: { bg: "#FFD500", emoji: "🟡" },
  NAO_MANDA: { bg: "#FF3366", emoji: "🔴" },
};

const HistoryPrints = ({ item, token }) => {
  const [srcs, setSrcs] = useState(null);
  useEffect(() => {
    let urls = [];
    Promise.all(item.images.map((im) => axios.get(`${API}/history/${item.id}/image/${im.index}`,
      { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then((r) => URL.createObjectURL(r.data)).catch((e) => { console.error("load print failed:", e); return null; })))
      .then((u) => { urls = u.filter(Boolean); setSrcs(urls); });
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [item, token]);
  if (!srcs) return <div className="flex justify-center py-3"><Loader2 className="animate-spin" size={18} /></div>;
  return <div className="mb-3"><PrintsThumbs prints={srcs.map((src, i) => ({ id: i, src }))} /></div>;
};

export const HistoryModal = ({ token, onClose }) => {
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [openId, setOpenId] = useState(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/history`, authHeader);
      setItems(data.items || []);
    } catch {
      console.error("load history failed");
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const deleteItem = async (id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
    try { await axios.delete(`${API}/history/${id}`, authHeader); } catch (e) { console.error("delete history item failed:", e); }
  };

  const clearAll = async () => {
    setClearing(true);
    try { await axios.delete(`${API}/history`, authHeader); setItems([]); } catch (e) { console.error("clear history failed:", e); }
    setClearing(false);
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString(lang === "pt" ? "pt-BR" : "en-US",
        { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" data-testid="history-modal">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg bg-[#F4F0E6] border-[4px] border-black rounded-2xl shadow-[8px_8px_0px_0px_#000] my-8"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b-[3px] border-black">
          <h2 className="font-display font-bold text-xl uppercase tracking-tight flex items-center gap-2">
            <Clock size={20} strokeWidth={2.5} /> {t.hist_title}
          </h2>
          <button data-testid="history-close-btn" onClick={onClose} className="border-[2px] border-black rounded-full p-1 bg-white hover:bg-[#FFD500] transition-colors">
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {items === null && (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin" size={26} /></div>
          )}
          {items !== null && items.length === 0 && (
            <p className="text-center text-[#666] font-semibold py-10" data-testid="history-empty">{t.hist_empty}</p>
          )}
          {items && items.map((it) => {
            if (it.kind === "conversation") {
              const open = openId === it.id;
              return (
                <div key={it.id} className="border-[2px] border-black rounded-xl bg-white p-3" data-testid="history-conversation-item">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-bold uppercase border-[2px] border-black rounded-full px-2 py-0.5 bg-[#BD00FF] text-white flex items-center gap-1">
                      <MessagesSquare size={12} strokeWidth={2.5} /> {it.source === "images" ? `${t.cv_hist_badge} · ${it.image_count} ${t.pr_prints_word}` : t.cv_hist_badge}
                    </span>
                    <button data-testid={`history-delete-${it.id}`} onClick={() => deleteItem(it.id)} aria-label="delete"
                      className="text-[#FF3366] hover:scale-110 transition-transform shrink-0">
                      <Trash2 size={16} strokeWidth={2.5} />
                    </button>
                  </div>
                  {it.result?.overview?.topic && <p className="mt-2 text-sm font-bold text-[#222]">{it.result.overview.topic}</p>}
                  <p className="mt-1 text-xs text-[#555] line-clamp-3">{it.result?.overview?.summary}</p>
                  <button data-testid={`history-conversation-toggle-${it.id}`} onClick={() => setOpenId(open ? null : it.id)}
                    className="mt-2 text-xs font-bold uppercase underline hover:text-[#BD00FF]">
                    {open ? t.cv_hist_hide : t.cv_hist_view}
                  </button>
                  {open && it.result && (
                    <div className="mt-3">
                      {it.images?.length > 0 && <HistoryPrints item={it} token={token} />}
                      <ConversationResult result={it.result} noFilter={it.no_filter}
                        onCopy={async () => { await navigator.clipboard.writeText(convoToText(it.result, t)); toast.success(t.copied); }} />
                    </div>
                  )}
                  <p className="mt-2 text-[10px] font-bold uppercase tracking-wider text-[#999]">{fmtDate(it.created_at)}</p>
                </div>
              );
            }
            const v = VERDICT_STYLE[it.verdict] || VERDICT_STYLE.ESPERA;
            return (
              <div key={it.id} className="border-[2px] border-black rounded-xl bg-white p-3" data-testid="history-item">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold uppercase border-[2px] border-black rounded-full px-2 py-0.5" style={{ background: v.bg }}>
                      {v.emoji} {t[`verdict_${it.verdict}`] || it.verdict}
                    </span>
                    {it.category?.label && (
                      <span className="text-xs font-semibold text-[#555]">{it.category.emoji} {it.category.label}</span>
                    )}
                  </div>
                  <button
                    data-testid={`history-delete-${it.id}`}
                    onClick={() => deleteItem(it.id)}
                    aria-label="delete"
                    className="text-[#FF3366] hover:scale-110 transition-transform shrink-0"
                  >
                    <Trash2 size={16} strokeWidth={2.5} />
                  </button>
                </div>
                <p className="mt-2 text-sm font-semibold text-[#222] line-clamp-2">"{it.message}"</p>
                {it.comment && <p className="mt-1 text-xs text-[#555] line-clamp-3">{it.comment}</p>}
                <p className="mt-2 text-[10px] font-bold uppercase tracking-wider text-[#999]">{fmtDate(it.created_at)}</p>
              </div>
            );
          })}
        </div>

        {items && items.length > 0 && (
          <div className="px-4 pb-4">
            <button
              data-testid="history-clear-btn"
              onClick={clearAll}
              disabled={clearing}
              className="w-full flex items-center justify-center gap-2 bg-white border-[2px] border-black rounded-xl py-2.5 text-sm font-bold uppercase tracking-wide shadow-[3px_3px_0px_0px_#000] hover:bg-[#FF3366] hover:text-white transition-colors disabled:opacity-60"
            >
              {clearing ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} strokeWidth={2.5} />} {t.hist_clear}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};
